/**
 * Aid-related constants for SF-28.
 * Trade value multipliers match SF-18 backend implementation.
 */
import type { ResourceAmounts } from '../types/aid';

// Tax estimation constants (mirror Property.TAX_THRESHOLD_MULTIPLIER and Property.MAX_TAX_RATE)
export const TAX_THRESHOLD_MULTIPLIER = 4.0;
export const MAX_TAX_RATE = 0.98;

// Trade value multipliers (gold-equivalent)
export const TRADE_VALUE_GOLD = 1.0;
export const TRADE_VALUE_FOOD = 0.05;
export const TRADE_VALUE_SOLDIER = 100.0;
export const TRADE_VALUE_RUNE = 3.0;

// Max shipment = networth × 4
export const MAX_SHIPMENT_MULTIPLIER = 4.0;

// Networth value approximations for frontend estimation
// These are used for UX only - backend does actual validation
export const NETWORTH_VALUES = {
  peasant: 1.0,
  soldier: 1.5,
  defenseSpecialist: 4.0, // Approx defense points
  offenseSpecialist: 3.0, // Approx offense points × 0.8
  elite: 6.0, // Average of 5-7
  thief: 4.0,
  wizard: 4.0,
  goldDivisor: 1000.0,
  barrenAcre: 15.0,
  buildingComplete: 55.0,
  buildingConstruction: 30.0,
};

/**
 * Calculate trade value of resources.
 */
export function calculateTradeValue(
  gold: number,
  food: number,
  soldiers: number,
  runes: number
): number {
  return (
    gold * TRADE_VALUE_GOLD +
    food * TRADE_VALUE_FOOD +
    soldiers * TRADE_VALUE_SOLDIER +
    runes * TRADE_VALUE_RUNE
  );
}

/**
 * Estimate recipient's tax rate from their trade balance and networth.
 * Mirrors TradeSystem.calculateTaxRate() on the backend. UX approximation only.
 */
export function estimateTaxRate(tradeBalance: number, networth: number): number {
  if (networth <= 0) return 0;
  const baseTaxRate = -TAX_THRESHOLD_MULTIPLIER * (tradeBalance / networth + TAX_THRESHOLD_MULTIPLIER);
  if (baseTaxRate < 0) return 0;
  return Math.min(baseTaxRate, MAX_TAX_RATE);
}

/**
 * Estimate province networth from available data.
 * This is an approximation for UX - backend does actual validation.
 */
export function estimateNetworth(
  gold: number,
  peasants: number,
  soldiers: number,
  defenseSpecialists: number,
  offenseSpecialists: number,
  elites: number,
  thieves: number,
  wizards: number,
  barrenAcres: number,
  buildingsComplete: number,
  buildingsConstruction: number
): number {
  const nw = NETWORTH_VALUES;
  return (
    peasants * nw.peasant +
    soldiers * nw.soldier +
    defenseSpecialists * nw.defenseSpecialist +
    offenseSpecialists * nw.offenseSpecialist +
    elites * nw.elite +
    thieves * nw.thief +
    wizards * nw.wizard +
    gold / nw.goldDivisor +
    barrenAcres * nw.barrenAcre +
    buildingsComplete * nw.buildingComplete +
    buildingsConstruction * nw.buildingConstruction
  );
}

const TRADE_VALUE_RATES: Record<keyof ResourceAmounts, number> = {
  gold: TRADE_VALUE_GOLD,
  food: TRADE_VALUE_FOOD,
  soldiers: TRADE_VALUE_SOLDIER,
  runes: TRADE_VALUE_RUNE,
};

/**
 * Headroom-aware Max fill for one resource field: the most units of `resource` that can be
 * added without pushing the total shipment trade value over `maxShipment`, given the CURRENT
 * amounts already entered for the other three resources. Clamped to [0, available].
 */
export function resolveMaxFill(
  resource: keyof ResourceAmounts,
  resourceAmounts: ResourceAmounts,
  available: number,
  maxShipment: number
): number {
  const rate = TRADE_VALUE_RATES[resource];
  const othersValue = calculateTradeValue(
    resource === 'gold' ? 0 : resourceAmounts.gold,
    resource === 'food' ? 0 : resourceAmounts.food,
    resource === 'soldiers' ? 0 : resourceAmounts.soldiers,
    resource === 'runes' ? 0 : resourceAmounts.runes
  );
  const remainingBudget = maxShipment - othersValue;
  if (remainingBudget <= 0) return 0;
  return Math.max(0, Math.min(available, Math.floor(remainingBudget / rate)));
}
